import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, ChevronDown, ChevronRight, ChevronLeft } from "lucide-react"
import { cn } from "@/lib/utils"
import { ThemeToggle } from "@/components/theme-toggle"
import { WalletConnectButton } from "@/components/wallet-connect-button"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import XIcon from "@mui/icons-material/X"
import TelegramIcon from "@mui/icons-material/Telegram"
import welephantLogo from "@/assets/tokens/welephant.svg"
import { useEthersContext } from "@/providers/EthersProvider"
import { Contract, Interface } from "ethers"
import { getChainProvider } from "@/lib/chainProvider"
import { FEATURES } from "@/config/features"

const STREAM_VAULT_ADDRESS = import.meta.env.VITE_STREAM_VAULT_ADDRESS
const GAME_CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID || 56)

const navAfterMining = [
  { name: "Swap", href: "/swap" },
  { name: "Stake", href: "/stake" },
  { name: "Bridge", href: "/bridge" },
]

const miningLinks = [
  { name: "Overview", href: "/mining" },
  { name: "Mine", href: "/mine" },
  { name: "Explore", href: "/explore" },
  { name: "Stats", href: "/stats" },
]

// Stats normally lives in the Mining dropdown; when Mining is off it
// surfaces as a top-level link so the token stats stay reachable.
const tokenLinks = FEATURES.mining
  ? navAfterMining
  : [...navAfterMining, { name: "Stats", href: "/stats" }]

export function Header() {
  const location = useLocation()
  const { address } = useEthersContext()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSubmenu, setMobileSubmenu] = useState<"streams" | "mining" | "about" | null>(null)
  const [hasDonated, setHasDeposited] = useState<boolean | null>(null)

  useEffect(() => {
    if (!FEATURES.streams || !address || !STREAM_VAULT_ADDRESS) { setHasDeposited(null); return }
    const iface = new Interface(["function donationCount(address) view returns (uint256)"])
    getChainProvider(GAME_CHAIN_ID).then(p => {
      const contract = new Contract(STREAM_VAULT_ADDRESS, iface, p)
      return contract.donationCount(address)
    }).then((count: bigint) => setHasDeposited(count > 0n))
      .catch(() => setHasDeposited(null))
  }, [address])

  const streamLabel = hasDonated ? "Your Stream" : "Start a Stream"
  const streamsLinks = [
    { name: "Browse", href: "/streams" },
    { name: streamLabel, href: "/stream" },
    { name: "Trending", href: "/trending" },
    { name: "Stream Stats", href: "/stream-stats" },
  ]

  const isStreamsActive =
    location.pathname === "/streams" ||
    location.pathname.startsWith("/streams/") ||
    location.pathname === "/stream" ||
    location.pathname.startsWith("/stream/") ||
    location.pathname === "/trending" ||
    location.pathname === "/stream-stats"

  const isMiningActive =
    location.pathname === "/mining" ||
    location.pathname === "/mine" ||
    location.pathname.startsWith("/mine/") ||
    location.pathname === "/explore" ||
    location.pathname.startsWith("/explore/") ||
    location.pathname === "/stats"

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center">
        {/* Logo / Wordmark */}
        <Link to="/" className="mr-4 md:mr-8 flex items-center space-x-2">
          <img src={welephantLogo} alt="WELEPHANT" className="h-7 w-7" />
          <span className="text-xl font-bold tracking-tight">WELEPHANT</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
          {/* Streams Dropdown */}
          {FEATURES.streams && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                className={cn(
                  "flex items-center gap-1 transition-colors hover:text-foreground/80 outline-none",
                  isStreamsActive ? "text-foreground" : "text-foreground/60"
                )}
              >
                Streams
                <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {streamsLinks.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link to={item.href}>{item.name}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mining Dropdown */}
          {FEATURES.mining && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                className={cn(
                  "flex items-center gap-1 transition-colors hover:text-foreground/80 outline-none",
                  isMiningActive ? "text-foreground" : "text-foreground/60"
                )}
              >
                Mining
                <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {miningLinks.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link to={item.href}>{item.name}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {tokenLinks.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "transition-colors hover:text-foreground/80",
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + "/")
                  ? "text-foreground"
                  : "text-foreground/60"
              )}
            >
              {item.name}
            </Link>
          ))}

          {/* About Dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              className={cn(
                "flex items-center gap-1 transition-colors hover:text-foreground/80 outline-none",
                location.pathname.startsWith("/docs") || location.pathname.startsWith("/blog")
                  ? "text-foreground"
                  : "text-foreground/60"
              )}
            >
              About
              <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/docs/overview">Docs</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/blog">Blog</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/decks">Pitch</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href="https://x.com/ElephantStatus" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <XIcon sx={{ fontSize: 16 }} />
                  Twitter
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="https://t.me/elephant_money" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <TelegramIcon sx={{ fontSize: 16 }} />
                  Telegram
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Right side */}
        <div className="ml-auto flex items-center space-x-2 md:space-x-4">
          <ThemeToggle />
          <div className="hidden sm:block">
            <WalletConnectButton />
          </div>
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileSubmenu(null) }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-background overflow-hidden">
          <div
            className="flex transition-transform duration-200 ease-in-out"
            style={{ transform: mobileSubmenu ? "translateX(-100%)" : "translateX(0)" }}
          >
            {/* Main menu panel */}
            <nav className="min-w-full container py-4 flex flex-col space-y-1">
              {FEATURES.streams && (
                <button
                  onClick={() => setMobileSubmenu("streams")}
                  className={cn(
                    "flex items-center gap-2 text-sm font-medium transition-colors hover:text-foreground/80 py-2 w-full text-left",
                    isStreamsActive ? "text-foreground" : "text-foreground/60"
                  )}
                >
                  Streams
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              )}

              {FEATURES.mining && (
                <button
                  onClick={() => setMobileSubmenu("mining")}
                  className={cn(
                    "flex items-center gap-2 text-sm font-medium transition-colors hover:text-foreground/80 py-2 w-full text-left",
                    isMiningActive ? "text-foreground" : "text-foreground/60"
                  )}
                >
                  Mining
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              )}

              {tokenLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-foreground/80 py-2",
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href + "/")
                      ? "text-foreground"
                      : "text-foreground/60"
                  )}
                >
                  {item.name}
                </Link>
              ))}

              <button
                onClick={() => setMobileSubmenu("about")}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium transition-colors hover:text-foreground/80 py-2 w-full text-left",
                  location.pathname.startsWith("/docs") || location.pathname.startsWith("/blog")
                    ? "text-foreground"
                    : "text-foreground/60"
                )}
              >
                About
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>

              <div className="pt-2 sm:hidden">
                <WalletConnectButton />
              </div>
            </nav>

            {/* Submenu panel */}
            <nav className="min-w-full container py-4 flex flex-col space-y-1">
              <button
                onClick={() => setMobileSubmenu(null)}
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground py-2 mb-1"
              >
                <ChevronLeft className="h-4 w-4" />
                {mobileSubmenu === "streams" ? "Streams" : mobileSubmenu === "mining" ? "Mining" : "About"}
              </button>

              {FEATURES.streams && mobileSubmenu === "streams" && streamsLinks.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-foreground/80 py-2",
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href + "/")
                      ? "text-foreground"
                      : "text-foreground/60"
                  )}
                >
                  {item.name}
                </Link>
              ))}

              {FEATURES.mining && mobileSubmenu === "mining" && miningLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-foreground/80 py-2",
                    location.pathname === item.href ? "text-foreground" : "text-foreground/60"
                  )}
                >
                  {item.name}
                </Link>
              ))}

              {mobileSubmenu === "about" && (
                <>
                  <Link
                    to="/docs/overview"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "text-sm font-medium transition-colors hover:text-foreground/80 py-2",
                      location.pathname.startsWith("/docs") ? "text-foreground" : "text-foreground/60"
                    )}
                  >
                    Docs
                  </Link>
                  <Link
                    to="/blog"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "text-sm font-medium transition-colors hover:text-foreground/80 py-2",
                      location.pathname.startsWith("/blog") ? "text-foreground" : "text-foreground/60"
                    )}
                  >
                    Blog
                  </Link>
                  <Link
                    to="/decks"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "text-sm font-medium transition-colors hover:text-foreground/80 py-2",
                      location.pathname.startsWith("/decks") ? "text-foreground" : "text-foreground/60"
                    )}
                  >
                    Pitch
                  </Link>
                  <a
                    href="https://x.com/ElephantStatus"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium transition-colors hover:text-foreground/80 py-2 text-foreground/60 flex items-center gap-2"
                  >
                    <XIcon sx={{ fontSize: 16 }} />
                    Twitter
                  </a>
                  <a
                    href="https://t.me/elephant_money"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium transition-colors hover:text-foreground/80 py-2 text-foreground/60 flex items-center gap-2"
                  >
                    <TelegramIcon sx={{ fontSize: 16 }} />
                    Telegram
                  </a>
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  )
}
